// Hideo - how a published video is doing, platform by platform (user request, 2/11/2026): a verdict (Bad / Average /
// Good: one tap on the Published videos card, VerdictButton.vue) and the numbers the user copies from each platform
// (views, likes, comments…: the video dialog's "Metrics" tab, MetricsPanel.vue). Typed by hand: the platforms' APIs
// need verifications and a server (docs/decisions.md), so Hideo reads nothing by itself. Stored in video.metrics =
// { <PubLinkKey>: { verdict, views, likes, …, watch, note, updatedAt } }; verdict values are stored ids, shown with t().
import { t } from '../i18n'
import type { PlatformMetrics, PubLinkKey, Verdict, Video } from '../data/types'
import { PUB_SOCIALS, cleanPubLinks } from './published'
import { isoDay } from '../utils/date'

/** [stored value, English label, text colour, background and border] */
export const VERDICTS: readonly (readonly [Verdict, string, string, string])[] = [
  ['bad', 'Bad', 'text-red-400', 'bg-red-500/15 border-red-500/40'],
  ['avg', 'Average', 'text-amber-400', 'bg-amber-500/15 border-amber-500/40'],
  ['good', 'Good', 'text-green-400', 'bg-green-500/15 border-green-500/40']
]
export const verdictInfo = (v: string | undefined) => VERDICTS.find(x => x[0] === v) || null
export const verdictLabel = (v: string | undefined): string => { const x = verdictInfo(v); return x ? t(x[1]) : '' }

/** The numbers of a platform: [field, English label] (watch time is a text, e.g. "2:15") */
export const METRIC_NUMBERS: readonly (readonly [keyof PlatformMetrics, string])[] = [
  ['views', 'Views'], ['likes', 'Likes'], ['comments', 'Comments'], ['shares', 'Shares'], ['saves', 'Saves'],
  ['followers', 'New followers'], ['retention', 'Retention (%)']
]
const NUM_KEYS = METRIC_NUMBERS.map(x => x[0])
const TEXT_MAX = 2000

/** A platform's metrics checked field by field (read-time default and before saving); null when nothing is left */
export function cleanPlatform(raw: unknown): PlatformMetrics | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>, out: PlatformMetrics = { verdict: verdictInfo(r.verdict as string) ? r.verdict as Verdict : '' }
  for (const k of NUM_KEYS) {
    const v = r[k], n = typeof v === 'string' ? Number(v.replace(/[.\s]/g, '').replace(',', '.')) : v
    if (typeof n === 'number' && Number.isFinite(n) && n >= 0 && !(typeof v === 'string' && !v.trim())) (out as unknown as Record<string, unknown>)[k] = Math.round(n * 100) / 100
  }
  for (const k of ['watch', 'note'] as const) if (typeof r[k] === 'string' && (r[k] as string).trim()) out[k] = (r[k] as string).trim().slice(0, TEXT_MAX)
  if (typeof r.updatedAt === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(r.updatedAt)) out.updatedAt = r.updatedAt
  const filled = out.verdict || NUM_KEYS.some(k => k in out) || out.watch || out.note
  return filled ? out : null
}
/** video.metrics checked: known platforms only, empty ones dropped */
export function cleanMetrics(raw: unknown): Partial<Record<PubLinkKey, PlatformMetrics>> {
  const out: Partial<Record<PubLinkKey, PlatformMetrics>> = {}
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out
  for (const [k] of PUB_SOCIALS) {
    const m = cleanPlatform((raw as Record<string, unknown>)[k])
    if (m) out[k] = m
  }
  return out
}

/** The platforms of a video: those with a link, then those with metrics but no link any more (PUB_SOCIALS order) */
export function metricPlatforms(v: Pick<Video, 'pubLinks' | 'metrics'>, extra: readonly string[] = []): PubLinkKey[] {
  const L = cleanPubLinks(v.pubLinks), M = v.metrics || {}
  return PUB_SOCIALS.map(s => s[0]).filter(k => L[k] || M[k] || extra.includes(k))
}

/** The card's verdict button: sets a platform's verdict (the same one again clears it), dated today. The caller saves */
export function setVerdict(v: Video, platform: PubLinkKey, verdict: Verdict | '', now = new Date()): void {
  const M = { ...(v.metrics || {}) }, cur = M[platform] || { verdict: '' as const }
  const next = cleanPlatform({ ...cur, verdict: cur.verdict === verdict ? '' : verdict, updatedAt: isoDay(now) })
  if (next) M[platform] = next
  else delete M[platform]
  v.metrics = M
}

/** How many platforms have a verdict (the tab's count) */
export const verdictCount = (m: Video['metrics']): number => Object.values(m || {}).filter(x => x && x.verdict).length

